import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { parseAiSummaryBullets } from '#src/app/pages/petitionDetails/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';

import type { AiSummaries } from '../aiSummaries';
import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionPageAiSummaryCardsExperiment'];

type Params = {
	experiment: Experiment;
	aiSummaries: AiSummaries | null;
	utilityContext: UtilityContext;
};

/**
 * Deliberately gated on whether the AI summary section will actually render (same bullets check the
 * component itself uses) rather than treated unconditionally: treating regardless would record an
 * exposure for a user who never sees either presentation (no summary, or a summary with no
 * parseable bullets), polluting the experiment's analysis population.
 */
export async function normalizePetitionPageAiSummaryCardsExperiment({
	experiment,
	aiSummaries,
	utilityContext,
}: Params): Promise<boolean> {
	if (parseAiSummaryBullets(aiSummaries?.summary).length === 0) {
		return false;
	}

	if (experiment.override) {
		// A QA/dev `?exo.` override already forced `variation`/`override: true` upstream. Pass it
		// through without calling `treatExperiment`: that hits the real Experiment Service treat
		// endpoint for the session UUID, which would register a real bucket assignment for a visit
		// that was never a genuine assignment -- polluting `experiment_variations` for that user.
		return experiment.variation === 'enabled';
	}

	await treatExperiment(utilityContext, experiment.experimentName);

	return experiment.variation === 'enabled';
}
